import { detailsType } from '#details/constants';
import { RecordValidator, useGenericValidator } from '#record/validator';
import { useAppSettings } from '#settings/settings';
import { RecordIssue } from '#submission/validator';
import { useT } from 'apprise-frontend-core/intl/language';
import { useL } from 'apprise-frontend-core/intl/multilang';
import { utils } from 'apprise-core/utils';
import { isBytestream } from 'apprise-frontend-streams/model';
import { useTagStore } from 'apprise-frontend-tags/tag/store';
import { photographsTypeCategory, photographType } from './constants';
import { PhotographsPatch, usePhotoQualityHelper } from './model';
import { Tag } from 'apprise-core/tag';
import { Asset } from '#submission/model';



export const usePhotographValidation = (): RecordValidator<PhotographsPatch> => {

    const validator = usePhotographValidator()

    return useGenericValidator(photographType, validator)
}


export const usePhotographValidator = (): RecordValidator<PhotographsPatch> => {

    const l = useL()
    const t = useT()

    const lowQualityMessage = usePhotoQualityHelper()

    const { photosRequired } = useAppSettings()

    const tags =  useTagStore()

    return data => data.reduce((acc, { patch, current }) => {

        const onlyPhotoPatch = patch.patchedSlots.length === 1 && patch.patchedSlots.some(ps => ps === 'photograph')

        // if the settings are set for 'Photo Required' or we are handling a Photo only submission the severity is 'Error'
        const defaultSeverity = (photosRequired || onlyPhotoPatch) ? 'error' : 'warning'

        const issues: RecordIssue[] = []

        const { photos = [] } = patch[photographType]


        const { photos:previousPhotos } = current?.[photographType] ?? {}

        // we expect photos if we have none to fallback to or key details have changed (name)
        const expectPhotos = previousPhotos && ( patch[detailsType] && [
            [patch.tenant, current?.tenant],
            [patch[detailsType]?.name, current?.[detailsType]?.name],
            [patch[detailsType]?.identifiers, current?.[detailsType]?.identifiers],
            [patch[detailsType]?.port, current?.[detailsType]?.port]

        ].find(([p, c]) => !utils().deepequals(p, c)))

        // Later in validation we validate all the tags of type photograph to see if a valid picture was provided,
        //  for this we sort the tags based on the uploaded bytestream to let the iteration start from possible valid photos.
        //  This allows us to set the proper severity if we are parsing a 'Photograph Only' submission and we have the settings
        //  for 'Photos Required' set to off. In this case we want the validation act with an Error if no photo is provided and as 
        //  warning for the missing photos if one is valid. Here the sort comes in help because the tag iteration can set the proper
        //  severity if one photo was priorly valid because if unsorted and, as example, only the second photo was provided, we would
        //  end up with an error on the first and a warning on the third.
        const phototags = (tags.allTagsOfCategory(photographsTypeCategory, true) ?? []).sort((pt1, _) => isBytestream(photos.find(p => p.type === pt1.id)?.ref) ? -1 : 1)

        let isAtLeastOnePhotoValid = false

        const validate = (tag: Tag, severity: 'error' | 'warning') => {

            const photoref = photos.find(p => p.type === tag.id)?.ref

            // we have at least a reference.
            if (photoref) {

                // resolved image, check quality.
                if (isBytestream(photoref)){

                    const message = lowQualityMessage(photoref, tag.id)

                    if (message)
                        issues.push({ message, type: 'warning', location: `${tag.id}-quality` })
                    else
                        isAtLeastOnePhotoValid = true 

                }
                    
                // unresolved reference.
                else  

                    issues.push({ message: t('valid.unresolved_photo', { ref: photoref }), type: severity, location: tag.id })


            }

            else
                 issues.push({ message: t( expectPhotos ? 'valid.expected_photo' : 'valid.missing_photo' , { type: l(tag.name) }), type: severity, location: tag.id })



        }

        // settings-driven global check in case there are no photos (missing or unresolved) 
        // AND we have no previous photo to fallback to.
        if (
            !photos.some(p => isBytestream(p.ref)) 
            && (!previousPhotos || !previousPhotos?.some(p => isBytestream(p.ref)))
        )
            issues.push({ message: t('valid.missing_photos'), type:  defaultSeverity })
        else {

            const resolvedPhotos = photos.filter(p => isBytestream(p.ref)).map(p => ({type: p.type, ref: p.ref as Asset}))

            resolvedPhotos.forEach((photo, i) => {
                const duplicate = resolvedPhotos.find((other, j) => j < i && other.ref.size === photo.ref.size && other.ref.properties.width === photo.ref.properties.width && other.ref.properties.height === photo.ref.properties.height)

                if (duplicate)
                    issues.push({ message: t('valid.duplicate_photo', { type: l(tags.lookupTag(duplicate.type)?.name) }), type: 'error', location: `${photo.type}`})
            })
            
            // if we have a patch only submission and at least one photo is valid the severity become 'warning'
            phototags.forEach(tag => validate(tag , !photosRequired && onlyPhotoPatch && isAtLeastOnePhotoValid ? 'warning' : defaultSeverity))

        }



        return { ...acc, [patch.id]: issues }

    }, {})
}
